import { api } from "@/lib/api";
import { CounselorProfile } from "@/types";

export interface CounselorStats {
  totalSessions: number;
  completedSessions: number;
  pendingRequests: number;
  connectedStudents: number;
  averageRating: number;
  audioAndVideoCallDuration: number;
  commonConcerns: string[];
  sessionsByType: Record<string, number>;
  sessionsByStatus: Record<string, number>;
}

export interface ConnectedStudent {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  grade?: string;
  schoolName?: string;
  profilePicture?: string | null;
  lastSession?: string;
  totalSessions: number;
  status: "active" | "inactive" | "graduated";
  interests: string[];
  parentName?: string;
  parentPhone?: string;
  joinedDate: string;
  upcomingSessions: number;
  averageRating: number;
}

export interface PendingRequest {
  id: string;
  type:
    | "session_request"
    | "session_review"
    | "profile_update"
    | "urgent_message";
  title: string;
  description: string;
  priority: "low" | "medium" | "high" | "urgent";
  dueDate?: string;
  studentId?: string;
  studentName?: string;
  sessionId?: string;
  createdAt: string;
  status?: string;
}

// Get current counselor profile
export const getCurrentCounselorProfile =
  async (): Promise<CounselorProfile> => {
    return api.get<CounselorProfile>("/profile/counselor/me");
  };

// Update current counselor profile
export const updateCurrentCounselorProfile = async (
  data: Partial<CounselorProfile>
): Promise<CounselorProfile> => {
  return api.patch<CounselorProfile>("/profile/counselor/me", data);
};

// Get counselor profile by ID (for admin use)
export const getCounselorProfile = async (
  userId: string
): Promise<CounselorProfile> => {
  return api.get<CounselorProfile>(`/profile/counselor/${userId}`);
};

// Update counselor profile by ID (for admin use)
export const updateCounselorProfile = async (
  userId: string,
  data: Partial<CounselorProfile>
): Promise<CounselorProfile> => {
  return api.patch<CounselorProfile>(`/profile/counselor/${userId}`, data);
};

// Update counselor availability
export const updateCounselorAvailability = async (
  userId: string,
  isAvailable: boolean
): Promise<void> => {
  return api.patch<void>(`/profile/counselor/availability/${userId}`, {
    isAvailable,
  });
};

// Get counselor statistics
export const getCounselorStats = async (
  userId: string,
  timeframe?: "today" | "week" | "month" | "all"
): Promise<CounselorStats> => {
  const params = timeframe && timeframe !== "all" ? { timeframe } : undefined;
  return api.get<CounselorStats>(`/profile/counselor/stats/${userId}`, params);
};

// Get connected students
export const getConnectedStudents = async (
  userId: string,
  params?: {
    search?: string;
    status?: "active" | "inactive" | "graduated";
    grade?: string;
    page?: number;
    limit?: number;
  }
): Promise<{
  students: ConnectedStudent[];
  total: number;
  page: number;
  limit: number;
}> => {
  return api.get(`/profile/counselor/connected-students/${userId}`, params);
};

// Get pending requests
export const getPendingRequests = async (
  userId: string
): Promise<PendingRequest[]> => {
  const response = await api.get<any[]>(
    `/profile/counselor/pending-requests/${userId}`
  );

  // Transform the API response to match the expected interface
  return response.map((item) => ({
    id: item.id,
    type: "session_request" as const,
    title: "Session Request",
    description: `${
      item.student?.fullName || "Student"
    } has requested a chat session`,
    priority: "medium" as const,
    studentId: item.studentId,
    studentName: item.student?.fullName || "Unknown Student",
    createdAt: item.createdAt,
    status: item.status,
  }));
};

// Get session history for counselor
export const getCounselorSessionHistory = async (
  userId: string,
  params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    startDate?: string;
    endDate?: string;
  }
) => {
  return api.get(`/profile/counselor/session-history/${userId}`, params);
};
